import React, {
	createContext, useContext, useMemo, useState,
} from 'react';
import TwoFactorAuthApiImpl from '../../data/remote/two-factor-api-impl';
import { ApiResponse } from 'common/domain/entities/api-response';
import useAuthBackoffice from './auth-backoffice.provider';
import { SimpleUserBackofficeRolesModel } from '../../data/dto/user-backoffice-model';

interface FetchState {
	loading: boolean;
	error: string;
	success: boolean
}

type TContextProps = {
	fetchState: FetchState;
	qrCodeUrl: string;
	setQrCodeUrl: (params: string) => void
	enableTwoFactorAuth: (userId: number) => Promise<boolean>;
	confirmTwoFactorAuth: (userId: number, code: string) => Promise<boolean>;
}

const TwoFactorAuthContext = createContext({} as TContextProps);

export const TwoFactorAuthBackofficeProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const [qrCodeUrl, setQrCodeUrl] = useState('');
	const [fetchState, setFetchState] = useState<FetchState>({
		loading: false,
		error: '',
		success: false,
	});
	const twoFactorApi = new TwoFactorAuthApiImpl();
	const { loggedUser, setUser } = useAuthBackoffice();

	const enableTwoFactorAuth = (userId: number) => {
		setFetchState((prev) => ({ ...prev, loading: true }));
		return twoFactorApi.enableTwoFactorAuth(userId).then((res) => {
			setQrCodeUrl(res.data.qrCodeImageUrl);
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setFetchState((prev: FetchState) => ({ ...prev, error: responseError.message }));
			return false;
		}).finally(() => {
			setFetchState({ ...fetchState, loading: false });
		});
	};

	const confirmTwoFactorAuth = (userId: number, code: string) => {
		setFetchState((prev) => ({ ...prev, loading: true }));
		return twoFactorApi.confirmTwoFactorAuth(userId, code).then((res) => {
			if (res.data) {
				setUser({
					...loggedUser, requiresTwoFactor: true,
				} as SimpleUserBackofficeRolesModel);
				setQrCodeUrl('');
			}
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setFetchState((prev: FetchState) => ({ ...prev, error: responseError.message }));
			return false;
		}).finally(() => {
			setFetchState({ ...fetchState, loading: false });
		});
	};

	const value = useMemo(() => ({
		enableTwoFactorAuth,
		confirmTwoFactorAuth,
		fetchState,
		qrCodeUrl,
		setQrCodeUrl,
	}), [fetchState, qrCodeUrl]);

	return <TwoFactorAuthContext.Provider value={value}>{children}</TwoFactorAuthContext.Provider>;
};

export default function useTwoFactorBackoffice() {
	return useContext(TwoFactorAuthContext);
}
